import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiError, createEdition, exportCatalogue, getCatalogue, importCatalogue, listEditions, setEditionStatus, updateClause } from "../services/AiService";
import { AcesCatalogue, AcesEdition } from "../types/ai";
import { saveBlob } from "../utils/secureFile";
import { ErrorAlert, Loading, PageShell } from "../component/aces/Shared";

/** SUPER_ADMIN: controlled standards reference data. The AI may only cite entries listed here. */
const AcesCatalogueScreen = () => {
  const { t } = useTranslation();
  const [editions, setEditions] = useState<AcesEdition[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [catalogue, setCatalogue] = useState<AcesCatalogue | null>(null);
  const [newCode, setNewCode] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const loadEditions = () => listEditions().then((e) => {
    setEditions(e);
    if (!selected && e.length) setSelected(e[0].id);
  }).catch((err) => setError(apiError(err)));
  useEffect(() => { loadEditions(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selected) getCatalogue(selected).then(setCatalogue).catch((e) => setError(apiError(e)));
  }, [selected]);

  const create = async () => {
    try {
      const e = await createEdition(newCode.trim(), newTitle.trim() || newCode.trim());
      setNewCode(""); setNewTitle("");
      await loadEditions();
      setSelected(e.id);
    } catch (err) {
      setError(apiError(err));
    }
  };

  const onImport = async (file?: File) => {
    if (!file || !selected) return;
    try {
      const text = await file.text();
      const kind = file.name.toLowerCase().endsWith(".csv") ? "csv" : "json";
      const result = await importCatalogue(selected, text, kind);
      setCatalogue(result);
      setNotice(t("aces.catalogue.imported", { clauses: result.clauses.length }));
    } catch (err) {
      setError(apiError(err));
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const edition = editions.find((e) => e.id === selected);
  const name = (list: { id: number; name?: string; code: string }[], id: number | null) => {
    const x = list.find((i) => i.id === id);
    return x ? x.code : "—";
  };

  return (
    <PageShell title={t("aces.catalogue.title")}>
      <ErrorAlert message={error} onClose={() => setError(null)} />
      {notice && <div className="aces-draft-note">{notice}</div>}
      <div className="aces-card">
        <div className="aces-actions">
          <select className="form-control" style={{ flex: "1 1 320px", maxWidth: 520 }} value={selected ?? ""} onChange={(e) => setSelected(Number(e.target.value))}>
            {editions.map((e) => <option key={e.id} value={e.id}>{e.code} — {e.title} ({t(`aces.catalogue.status.${e.status}`)})</option>)}
          </select>
          {edition && edition.status !== "ACTIVE" && (
            <button className="aces-btn success" onClick={() => setEditionStatus(edition.id, "ACTIVE").then(loadEditions).catch((e) => setError(apiError(e)))}>{t("aces.catalogue.activate")}</button>
          )}
          {edition && edition.status === "ACTIVE" && (
            <button className="aces-btn secondary" onClick={() => setEditionStatus(edition.id, "RETIRED").then(loadEditions).catch((e) => setError(apiError(e)))}>{t("aces.catalogue.retire")}</button>
          )}
          {edition && (
            <>
              <input ref={fileRef} type="file" accept=".json,.csv" style={{ display: "none" }} onChange={(e) => onImport(e.target.files?.[0])} />
              <button className="aces-btn secondary" onClick={() => fileRef.current?.click()}><i className="ri-upload-2-line"></i> {t("aces.catalogue.import")}</button>
              <button className="aces-btn secondary" onClick={() => exportCatalogue(edition.id).then((b) => saveBlob(b, `aces-${edition.code}.json`)).catch((e) => setError(apiError(e)))}>
                <i className="ri-download-2-line"></i> {t("aces.catalogue.export")}
              </button>
            </>
          )}
        </div>
        <p className="aces-muted mt-2 mb-0">{t("aces.catalogue.importHint")}</p>
      </div>
      <div className="aces-card">
        <h5>{t("aces.catalogue.newEdition")}</h5>
        <div className="aces-actions">
          <input className="form-control" style={{ maxWidth: 200 }} placeholder={t("aces.catalogue.code")} value={newCode} onChange={(e) => setNewCode(e.target.value)} />
          <input className="form-control" style={{ maxWidth: 320 }} placeholder={t("aces.catalogue.editionTitle")} value={newTitle} onChange={(e) => setNewTitle(e.target.value)} />
          <button className="aces-btn" disabled={!newCode.trim()} onClick={create}>{t("aces.common.add")}</button>
        </div>
      </div>
      {selected && !catalogue ? <Loading /> : catalogue && (
        <>
          <div className="aces-grid-3">
            <div className="aces-card"><h5>{t("aces.catalogue.stages")} ({catalogue.stages.length})</h5><ul>{catalogue.stages.map((s) => <li key={s.id}>{s.code} — {s.name}</li>)}</ul></div>
            <div className="aces-card"><h5>{t("aces.catalogue.processes")} ({catalogue.processes.length})</h5><ul>{catalogue.processes.map((p) => <li key={p.id}>{p.code} — {p.name}</li>)}</ul></div>
            <div className="aces-card"><h5>{t("aces.catalogue.ccps")} ({catalogue.ccps.length})</h5><ul>{catalogue.ccps.map((c) => <li key={c.id}>{c.code} — {c.name}</li>)}</ul></div>
          </div>
          <div className="__defaultTable">
            <table>
              <thead>
                <tr><th>{t("aces.catalogue.ref")}</th><th>{t("aces.catalogue.clauseTitle")}</th><th>{t("aces.catalogue.summary")}</th><th>{t("aces.ctx.process")}</th><th>{t("aces.finding.stage")}</th><th>CCP</th><th>{t("aces.sites.active")}</th></tr>
              </thead>
              <tbody>
                {catalogue.clauses.map((c) => (
                  <tr key={c.id} style={{ opacity: c.active ? 1 : 0.5 }}>
                    <td>{c.refCode}</td><td>{c.title}</td><td>{c.requirementSummary || "—"}</td>
                    <td>{name(catalogue.processes, c.processId)}</td><td>{name(catalogue.stages, c.stageId)}</td><td>{name(catalogue.ccps, c.ccpId)}</td>
                    <td>
                      <input type="checkbox" className="form-check-input" checked={c.active}
                        onChange={(e) => updateClause(c.id, { active: e.target.checked }).then(() => getCatalogue(catalogue.edition.id).then(setCatalogue)).catch((err) => setError(apiError(err)))} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </PageShell>
  );
};

export default AcesCatalogueScreen;
